/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: context.ts (Tenant & Domain Context Store)
 🗓️ Created: 2025-10-08
 🧩 Description:
    Store for managing selected tenant and domain context across
    the application for multi-tenant telephony operations.

 🔧 Notes:
    - Manages current selected tenant and domain
    - Persists selection in localStorage
    - Provides helper functions for context switching
    - Integrates with API calls for domain-specific operations

 🔁 Last Updated: 2025-10-08 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface TenantInfo {
  id: string;
  name: string;
  slug: string;
}

export interface DomainInfo {
  id: number;
  domain: string;
  description?: string;
}

interface ContextState {
  selectedTenant: TenantInfo | null;
  selectedDomain: DomainInfo | null;
  availableTenants: TenantInfo[];
  availableDomains: DomainInfo[];
}

interface ContextActions {
  setSelectedTenant: (tenant: TenantInfo | null) => void;
  setSelectedDomain: (domain: DomainInfo | null) => void;
  setAvailableTenants: (tenants: TenantInfo[]) => void;
  setAvailableDomains: (domains: DomainInfo[]) => void;
  clearContext: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initializeFromUser: (user: any) => void;
}

type ContextStore = ContextState & ContextActions;

export const useContextStore = create<ContextStore>()(
  persist(
    (set, get) => ({
      // State
      selectedTenant: null,
      selectedDomain: null,
      availableTenants: [],
      availableDomains: [],

      // Actions
      setSelectedTenant: (tenant) => {
        set({
          selectedTenant: tenant,
          // Clear domain when tenant changes
          selectedDomain: null,
          availableDomains: []
        });
      },

      setSelectedDomain: (domain) => {
        set({ selectedDomain: domain });
      },

      setAvailableTenants: (tenants) => {
        set({ availableTenants: tenants });
      },

      setAvailableDomains: (domains) => {
        set({ availableDomains: domains });
      },

      clearContext: () => {
        set({
          selectedTenant: null,
          selectedDomain: null,
          availableTenants: [],
          availableDomains: []
        });
      },

      initializeFromUser: (user) => {
        if (user?.tenant) {
          const currentState = get();

          // Only initialize if not already set
          if (!currentState.selectedTenant) {
            set({
              selectedTenant: {
                id: user.tenant.id,
                name: user.tenant.name,
                slug: user.tenant.slug
              }
            });
          }
        }
      }
    }),
    {
      name: 'ring2all-context-storage',
      partialize: (state) => ({
        selectedTenant: state.selectedTenant,
        selectedDomain: state.selectedDomain
      })
    }
  )
);

